import { motion } from "framer-motion";
import { Calendar, ArrowRight, TrendingUp, Newspaper, Video, Tag } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const blogPosts = [
  {
    category: "Industry News",
    title: "India's Basmati Rice Exports Hit Record High in 2025",
    excerpt: "Indian basmati rice exports surged to new heights this year, driven by growing demand from Africa and the Middle East. P.S.IMPEX has been at the forefront of this export boom.",
    date: "Dec 10, 2025",
    readTime: "4 min read",
    tag: "Exports",
  },
  {
    category: "Industry News",
    title: "APEDA Introduces New Quality Standards for Rice Exporters",
    excerpt: "The Agricultural and Processed Food Products Export Development Authority has announced updated quality benchmarks for rice exporters, raising the bar for Indian rice in global markets.",
    date: "Nov 28, 2025",
    readTime: "3 min read",
    tag: "Regulations",
  },
  {
    category: "Industry News",
    title: "Global Rice Demand Expected to Grow 12% by 2027",
    excerpt: "A new industry report projects significant growth in global rice consumption, particularly in African and Southeast Asian markets where P.S.IMPEX has strong presence.",
    date: "Nov 15, 2025",
    readTime: "5 min read",
    tag: "Market Trends",
  },
  {
    category: "Industry News",
    title: "Punjab's Paddy Harvest Sets New Record This Season",
    excerpt: "The latest kharif season has seen exceptional yields across Punjab's paddy belt, ensuring a robust supply of premium basmati rice for the upcoming export season.",
    date: "Oct 30, 2025",
    readTime: "3 min read",
    tag: "Agriculture",
  },
  {
    category: "Industry News",
    title: "Middle East Rice Imports Surge Amid Growing Population",
    excerpt: "Gulf nations are importing record quantities of Indian basmati rice as the region's population grows and demand for premium grains increases steadily.",
    date: "Oct 18, 2025",
    readTime: "4 min read",
    tag: "Market Trends",
  },
  {
    category: "Industry News",
    title: "Sustainable Rice Farming Practices Gain Traction in India",
    excerpt: "Indian rice farmers are increasingly adopting sustainable and organic farming methods, aligning with global demand for eco-friendly agricultural products.",
    date: "Oct 5, 2025",
    readTime: "5 min read",
    tag: "Sustainability",
  },
];

const vlogs = [
  {
    title: "Inside Our Rice Processing Facility",
    desc: "Take a virtual tour of the Fab Crops state-of-the-art rice milling and sortex facility where every grain is perfected.",
    duration: "8:32",
    tag: "Behind the Scenes",
  },
  {
    title: "From Paddy Field to Export Container",
    desc: "Follow the journey of premium 1121 Basmati rice from the farms of Punjab to shipping containers headed for global markets.",
    duration: "12:15",
    tag: "Supply Chain",
  },
  {
    title: "Quality Testing at Fab Crops Lab",
    desc: "Our quality assurance team demonstrates the rigorous testing process that every batch of Fab Crops rice undergoes before export.",
    duration: "6:48",
    tag: "Quality",
  },
  {
    title: "Understanding Basmati Rice Varieties",
    desc: "A comprehensive guide to the different basmati rice varieties — 1121, 1718, 1509, 1401 and Pusa — and what makes each unique.",
    duration: "10:20",
    tag: "Education",
  },
  {
    title: "Visiting Our Partner Farms in Punjab",
    desc: "Join founder Manjot Singh as he visits the paddy farms that supply Fab Crops with the finest basmati rice in India.",
    duration: "15:40",
    tag: "Farm Visit",
  },
  {
    title: "Packaging & Branding for Export Markets",
    desc: "Learn how Fab Crops customises packaging for different international markets while maintaining brand quality and consistency.",
    duration: "7:55",
    tag: "Exports",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function BlogPage() {
  return (
    <div data-testid="blog-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Insights & Updates
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-brand-green leading-[0.95] mb-6">
              News & <span className="italic text-brand-gold">Vlogs</span>
            </h1>
            <p className="text-brand-muted text-base sm:text-lg font-sans max-w-2xl leading-relaxed">
              Stay informed with the latest industry news, market trends, and
              behind-the-scenes content from Fab Crops and the global rice trade.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section data-testid="blog-tabs" className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Tabs defaultValue="news" className="w-full">
            <TabsList
              data-testid="blog-tabs-list"
              className="bg-brand-sand border border-brand-gold/10 rounded-full p-1 mb-12 w-fit"
            >
              <TabsTrigger
                value="news"
                data-testid="tab-news"
                className="rounded-full px-8 py-3 text-sm font-sans font-medium data-[state=active]:bg-brand-green data-[state=active]:text-brand-bone"
              >
                <Newspaper size={16} className="mr-2" />
                Industry News
              </TabsTrigger>
              <TabsTrigger
                value="vlogs"
                data-testid="tab-vlogs"
                className="rounded-full px-8 py-3 text-sm font-sans font-medium data-[state=active]:bg-brand-green data-[state=active]:text-brand-bone"
              >
                <Video size={16} className="mr-2" />
                Vlogs
              </TabsTrigger>
            </TabsList>

            {/* News Tab */}
            <TabsContent value="news" data-testid="news-content">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogPosts.map((post, i) => (
                  <motion.article
                    key={i}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={i}
                    className="group bg-white rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(26,54,38,0.04)] border border-brand-gold/10 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(26,54,38,0.08)] transition-all duration-500"
                  >
                    <div className="h-2 bg-brand-gold" />
                    <div className="p-8">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="px-3 py-1 bg-brand-sand text-brand-green text-xs font-sans font-medium rounded-full flex items-center gap-1">
                          <Tag size={12} />
                          {post.tag}
                        </span>
                      </div>
                      <h3 className="font-serif text-xl font-medium text-brand-green mb-3 group-hover:text-brand-gold transition-colors leading-snug">
                        {post.title}
                      </h3>
                      <p className="text-brand-muted text-sm font-sans leading-relaxed mb-6">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-brand-muted text-xs font-sans">
                          <Calendar size={14} />
                          {post.date}
                          <span className="mx-1">|</span>
                          {post.readTime}
                        </div>
                        <span className="text-brand-green group-hover:text-brand-gold transition-colors">
                          <ArrowRight size={18} />
                        </span>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </TabsContent>

            {/* Vlogs Tab */}
            <TabsContent value="vlogs" data-testid="vlogs-content">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {vlogs.map((vlog, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={i}
                    className="group bg-white rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(26,54,38,0.04)] border border-brand-gold/10 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(26,54,38,0.08)] transition-all duration-500"
                  >
                    {/* Video Placeholder */}
                    <div className="relative h-48 bg-brand-green flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-brand-gold/20 flex items-center justify-center group-hover:bg-brand-gold/30 transition-colors">
                        <Video size={28} className="text-brand-gold" />
                      </div>
                      <span className="absolute bottom-3 right-3 px-2 py-1 bg-brand-dark/70 text-brand-bone text-xs font-sans rounded">
                        {vlog.duration}
                      </span>
                    </div>
                    <div className="p-6">
                      <span className="px-3 py-1 bg-brand-sand text-brand-green text-xs font-sans font-medium rounded-full inline-flex items-center gap-1 mb-3">
                        <TrendingUp size={12} />
                        {vlog.tag}
                      </span>
                      <h3 className="font-serif text-lg font-medium text-brand-green mb-2 group-hover:text-brand-gold transition-colors leading-snug">
                        {vlog.title}
                      </h3>
                      <p className="text-brand-muted text-sm font-sans leading-relaxed">
                        {vlog.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section data-testid="newsletter-section" className="py-24 lg:py-32 bg-brand-green">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Stay Updated
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-bone leading-tight mb-6">
              Never Miss an Update
            </h2>
            <p className="text-brand-bone/70 text-base font-sans max-w-2xl mx-auto mb-10 leading-relaxed">
              Follow us for the latest industry insights, rice market trends,
              and exclusive behind-the-scenes content from Fab Crops.
            </p>
            <div className="flex items-center justify-center gap-3 text-brand-bone/50 text-sm font-sans">
              <Newspaper size={16} className="text-brand-gold" />
              More content coming soon
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
